import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, InputAdornment, TextField, Grid } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { IAdminUser, ICreateAdminUser, IUpdateAdminUser } from "./IAdminUser";

interface AdminUserDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (user: ICreateAdminUser | IUpdateAdminUser) => void;
    initialData?: Partial<IAdminUser>;
    isEdit?: boolean;
}

const validateEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const validateMobile = (mobile: string) =>
    /^[0-9]{10,15}$/.test(mobile);

export const AdminUserDialog: React.FC<AdminUserDialogProps> = ({
    open,
    onClose,
    onSave,
    initialData = {},
    isEdit = false,
}) => {
    const [form, setForm] = useState<ICreateAdminUser | IUpdateAdminUser>({
        userName: "",
        password: "",
        address: "",
        mobileNumber: "",
        email: "",
        ...initialData,
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({
                userName: initialData?.userName || "",
                password: "",
                address: initialData?.address || "",
                mobileNumber: initialData?.mobileNumber || "",
                email: initialData?.email || "",
                ...(isEdit && initialData?.userId ? { userId: initialData.userId } : {}),
            });
            setErrors({});
            setShowPassword(false);
        }
    }, [open, isEdit]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const validate = () => {
        const newErrors: { [key: string]: string } = {};
        if (!form.userName.trim()) newErrors.userName = "User Name is required";
        if (!isEdit && !form.password.trim()) newErrors.password = "Password is required";
        if (!form.mobileNumber.trim()) newErrors.mobileNumber = "Mobile Number is required";
        else if (!validateMobile(form.mobileNumber)) newErrors.mobileNumber = "Invalid mobile number";
        if (!form.email.trim()) newErrors.email = "Email is required";
        else if (!validateEmail(form.email)) newErrors.email = "Invalid email address";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validate()) {
            onSave(form);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? "Edit Admin User" : "Add Admin User"}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            label="User Name"
                            name="userName"
                            value={form.userName}
                            onChange={handleChange}
                            fullWidth
                            required
                            error={!!errors.userName}
                            helperText={errors.userName}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={form.password}
                            onChange={handleChange}
                            fullWidth
                            required={!isEdit}
                            error={!!errors.password}
                            helperText={isEdit ? (errors.password || "Leave blank to keep existing password") : errors.password}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(p => !p)} edge="end" aria-label="toggle password visibility">
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            fullWidth
                            required
                            error={!!errors.email}
                            helperText={errors.email}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Mobile Number"
                            name="mobileNumber"
                            value={form.mobileNumber}
                            onChange={handleChange}
                            fullWidth
                            required
                            error={!!errors.mobileNumber}
                            helperText={errors.mobileNumber}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Address"
                            name="address"
                            value={form.address}
                            onChange={handleChange}
                            fullWidth
                            multiline
                            rows={2}
                        />
                    </Grid>
                    
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained">{isEdit ? "Update" : "Create"}</Button>
            </DialogActions>
        </Dialog>
    );
};